/* eslint-disable @typescript-eslint/explicit-module-boundary-types */
import React from 'react';
/** Given a storage engine and an optional namespace, return Providers which is a component grouping all the Provider registred using the create function */
const Storer = ({ ns = '@', engine, migration }) => {
    const versionPersistKey = 'Storer.version';
    const key = (x) => `${ns}${x}`;
    async function get(k) {
        try {
            const raw = await engine.getItem(key(k));
            return raw != null ? JSON.parse(raw) : null;
        }
        catch (e) {
            return null;
        }
    }
    async function set(k, v) {
        try {
            await engine.setItem(key(k), JSON.stringify(v));
            return v;
        }
        catch (e) {
            return null;
        }
    }
    async function remove(k) {
        try {
            await engine.removeItem(key(k));
            return null;
        }
        catch (e) {
            return null;
        }
    }
    let wasProvidersRendered = false;
    /** Grab all the hydrated values from the engine and give them to each provider */
    const Providers = ({ children }) => {
        wasProvidersRendered = true;
        const persistedProvidersEntries = Object.entries(providersRegistry.byPersistKey);
        const [hydratedStates, sethydratedStates] = React.useState();
        React.useEffect(() => {
            get(versionPersistKey).then(async (persistedVersion) => {
                const shouldRunMigration = persistedVersion !== null && persistedVersion !== migration.version;
                /** This is important to not filter persistedProvidersEntries to hydrate otherwise because we rely on their ordering */
                const newHydratedStates = await Promise.all(persistedProvidersEntries.map(([k]) => shouldRunMigration && !migration.keep.includes(k) ? null : get(k)));
                sethydratedStates(newHydratedStates);
                await set(versionPersistKey, migration.version);
            });
        }, []);
        return (React.createElement(React.Fragment, null, providersRegistry.persistLess.reduce((acc, PersistLessProvider) => {
            return React.createElement(PersistLessProvider, null, acc);
        }, hydratedStates
            ? persistedProvidersEntries.reduce((acc, [, Provider], i) => {
                return React.createElement(Provider, { hydratedState: hydratedStates[i] }, acc);
            }, children)
            : null)));
    };
    const providersRegistry = {
        byPersistKey: {},
        persistLess: [],
        /** Register a provider with optional persistance  */
        register(Provider, persistKey) {
            const ProviderToMakeTsHappy = Provider;
            if (persistKey)
                providersRegistry.byPersistKey[persistKey] = ProviderToMakeTsHappy;
            else
                providersRegistry.persistLess.push(ProviderToMakeTsHappy);
        }
    };
    /** Given a piece of state, create a context, a provider with optional persistance, register it, and return a tuple of a hook to use the newly created context inside react and a getter function to get the value outside react */
    function create(defaultState, options) {
        const { persistKey } = options || {};
        if (wasProvidersRendered)
            throw new Error('Do not use create() after Providers rendering');
        let outsideState;
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        const ctx = React.createContext([defaultState, () => { }]);
        /** A provider for an atom of state */
        /** I am unable to get TS to understand the flush property is set after assignation */
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        //@ts-ignore
        const Provider = ({ hydratedState, children }) => {
            const originalValue = React.useState(hydratedState || defaultState);
            const [state, setState] = originalValue;
            const [didHydrateOutsideState, setDidHydrateOutsideState] = React.useState(false);
            React.useEffect(() => {
                if (persistKey)
                    set(persistKey, state);
            }, [state]);
            /** Important to use a ref otherwise we may break hooks depending on the setStater function which reference should not change */
            const setStater = React.useRef((setStateAction) => {
                if (typeof setStateAction === 'function') {
                    const computedNextState = setStateAction(state);
                    outsideState = computedNextState;
                }
                else {
                    outsideState = setStateAction;
                }
                return setState(setStateAction);
            });
            const value = React.useMemo(() => [state, setStater.current], [originalValue]);
            React.useEffect(() => {
                outsideState = state;
                setDidHydrateOutsideState(true);
                /** TODO: find a way to type this correctly */
                /** TODO: maybe the flush function assignation should be run on each setState change ? */
                Provider.flush = () => setStater.current(defaultState);
            }, []);
            return React.createElement(ctx.Provider, { value: value }, didHydrateOutsideState ? children : null);
        };
        Provider.displayName = `Provider(${persistKey})`;
        providersRegistry.register(Provider, persistKey);
        const useCtx = () => React.useContext(ctx);
        const getter = () => outsideState;
        return [useCtx, getter];
    }
    /** Remove all the state stored by the engine */
    const flush = async (options) => Promise.all(providersRegistry.persistLess
        .map(async (provider) => provider.flush())
        .concat(Object.entries(providersRegistry.byPersistKey).map(async ([k, provider]) => {
        if (!options?.except.includes(k)) {
            provider.flush();
            await remove(k);
        }
    })));
    return {
        Providers,
        create,
        flush
    };
};
export default Storer;
